import { Injectable, inject } from '@angular/core';
import { ToastrService } from 'ngx-toastr';

type NotificationCtor = typeof Notification;

/**
 * Silences browser notifications while the exam is running. The page's own
 * Notification API (constructor, permission prompt and service-worker
 * showNotification) is swapped for a denied no-op, and any toasts left over
 * from before the exam are cleared. Notifications raised by other sites or
 * the OS can't be suppressed from a web page — only by the browser/OS
 * (e.g. Do Not Disturb).
 */
@Injectable({ providedIn: 'root' })
export class ExamNotificationBlockerService {
  private readonly toastr = inject(ToastrService);

  private blocked = false;
  private originalNotification: NotificationCtor | undefined;
  private originalShowNotification: ServiceWorkerRegistration['showNotification'] | undefined;

  block(): void {
    if (this.blocked) {
      return;
    }

    this.blocked = true;
    this.toastr.clear();

    if ('Notification' in window) {
      this.originalNotification = window.Notification;
      window.Notification = createBlockedNotification();
    }

    if (typeof ServiceWorkerRegistration !== 'undefined') {
      this.originalShowNotification = ServiceWorkerRegistration.prototype.showNotification;
      ServiceWorkerRegistration.prototype.showNotification = () => Promise.resolve();
    }
  }

  unblock(): void {
    if (!this.blocked) {
      return;
    }

    this.blocked = false;

    if (this.originalNotification) {
      window.Notification = this.originalNotification;
      this.originalNotification = undefined;
    }

    if (this.originalShowNotification) {
      ServiceWorkerRegistration.prototype.showNotification = this.originalShowNotification;
      this.originalShowNotification = undefined;
    }
  }
}

/** Stand-in that reports permission as denied and never displays anything. */
function createBlockedNotification(): NotificationCtor {
  const BlockedNotification = function () {
    return new EventTarget();
  } as unknown as NotificationCtor;

  Object.defineProperty(BlockedNotification, 'permission', { get: () => 'denied' });
  Object.defineProperty(BlockedNotification, 'maxActions', { get: () => 0 });
  BlockedNotification.requestPermission = () => Promise.resolve('denied');

  return BlockedNotification;
}
